import axios, { AxiosInstance, CancelToken, AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { ApiException } from "./ClientDTO";
import { AppointmentDashboardDTO, ReceiptDashboardDTO, ServiceDashboardDTO, MedicalPackageDashboardDTO, PatientHistoryItemDTO, ShoppingCartDashboardDTO, ReceiptServiceForPatientDTO } from "./DashboardWidgetDTO";
import { MedicalAnalysisResultMedicalAnalysisTestDTO } from "./MedicalAnalysisDTO";

export class DashboardWidgetsClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {

      this.instance = instance || axios.create();
      this.instance.interceptors.request.use(
        config => {
            const token = localStorage.getItem('accessToken');
            if (token) {
                if (!config.headers) {
                    config.headers = {};
                }
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );
      this.baseUrl = baseUrl ?? "";

  }

  /**
   * @param patientId (optional) 
   * @return OK
   */
  appointments(patientId?: number | undefined, cancelToken?: CancelToken): Promise<AppointmentDashboardDTO[]> {
      let url_ = this.baseUrl + "/api/widget/appointments?";
      if (patientId === null)
          throw new Error("The parameter 'patientId' cannot be null.");
      else if (patientId !== undefined)
          url_ += "patientId=" + encodeURIComponent("" + patientId) + "&";
      url_ = url_.replace(/[?&]$/, "");

      let options_: AxiosRequestConfig = {
          method: "GET",
          url: url_,
          headers: {
              "Accept": "text/plain"
          },
          cancelToken
      };

      return this.instance.request(options_).catch((_error: any) => {
          if (isAxiosError(_error) && _error.response) {
              return _error.response;
          } else {
              throw _error;
          }
      }).then((_response: AxiosResponse) => {
          return this.processAppointments(_response);
      });
  }

  protected processAppointments(response: AxiosResponse): Promise<AppointmentDashboardDTO[]> {
      const status = response.status;
      let _headers: any = {};
      if (response.headers && typeof response.headers === "object") {
          for (const k in response.headers) {
              if (response.headers.hasOwnProperty(k)) {
                  _headers[k] = response.headers[k];
              }
          }
      }
      if (status === 200) {
          const _responseText = response.data;
          let result200: any = null;
          let resultData200  = _responseText;
          if (Array.isArray(resultData200)) {
              result200 = [] as any;
              for (let item of resultData200)
                  result200!.push(AppointmentDashboardDTO.fromJS(item));
          }
          else {
              result200 = <any>null;
          }
          return Promise.resolve<AppointmentDashboardDTO[]>(result200);

      } else if (status !== 200 && status !== 204) {
          const _responseText = response.data;
          return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      }
      return Promise.resolve<AppointmentDashboardDTO[]>(null as any);
  }

  /**
   * @param patientId (optional) 
   * @return OK
   */
  payments(patientId?: number | undefined, cancelToken?: CancelToken): Promise<ReceiptDashboardDTO[]> {
      let url_ = this.baseUrl + "/api/widget/payments?";
      if (patientId === null)
          throw new Error("The parameter 'patientId' cannot be null.");
      else if (patientId !== undefined)
          url_ += "patientId=" + encodeURIComponent("" + patientId) + "&";
      url_ = url_.replace(/[?&]$/, "");

      let options_: AxiosRequestConfig = {
          method: "GET",
          url: url_,
          headers: {
              "Accept": "text/plain"
          },
          cancelToken
      };

      return this.instance.request(options_).catch((_error: any) => {
          if (isAxiosError(_error) && _error.response) {
              return _error.response;
          } else {
              throw _error;
          }
      }).then((_response: AxiosResponse) => {
          return this.processPayments(_response);
      });
  }

  protected processPayments(response: AxiosResponse): Promise<ReceiptDashboardDTO[]> {
      const status = response.status;
      let _headers: any = {};
      if (response.headers && typeof response.headers === "object") {
          for (const k in response.headers) {
              if (response.headers.hasOwnProperty(k)) {
                  _headers[k] = response.headers[k];
              }
          }
      }
      if (status === 200) {
          const _responseText = response.data;
          let result200: any = null;
          let resultData200  = _responseText;
          if (Array.isArray(resultData200)) {
              result200 = [] as any;
              for (let item of resultData200)
                  result200!.push(ReceiptDashboardDTO.fromJS(item));
          }
          else {
              result200 = <any>null;
          }
          return Promise.resolve<ReceiptDashboardDTO[]>(result200);

      } else if (status !== 200 && status !== 204) {
          const _responseText = response.data;
          return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      }
      return Promise.resolve<ReceiptDashboardDTO[]>(null as any);
  }

    /**
     * @param patientId (optional) 
     * @return OK
     */
    shoppingcarts(patientId?: number | undefined, cancelToken?: CancelToken): Promise<ShoppingCartDashboardDTO[]> {
        let url_ = this.baseUrl + "/api/widget/shoppingcarts?";
        if (patientId === null)
            throw new Error("The parameter 'patientId' cannot be null.");
        else if (patientId !== undefined)
            url_ += "patientId=" + encodeURIComponent("" + patientId) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: {
                "Accept": "text/plain"
            },
            cancelToken
        };

        return this.instance.request(options_).catch((_error: any) => {
            if (isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processShoppingcarts(_response);
        });
    }

    protected processShoppingcarts(response: AxiosResponse): Promise<ShoppingCartDashboardDTO[]> {
        const status = response.status;
        let _headers: any = {};
        if (response.headers && typeof response.headers === "object") {
            for (const k in response.headers) {
                if (response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if (status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200  = _responseText;
            if (Array.isArray(resultData200)) {
                result200 = [] as any;
                for (let item of resultData200)
                    result200!.push(ShoppingCartDashboardDTO.fromJS(item));
            }
            else {
                result200 = <any>null;
            }
            return Promise.resolve<ShoppingCartDashboardDTO[]>(result200);

        } else if (status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
        }
        return Promise.resolve<ShoppingCartDashboardDTO[]>(null as any);
    }
  /**
   * @param receiptServiceId (optional) 
   * @return OK
   */
  paymentsservices(receiptServiceId?: number | undefined, cancelToken?: CancelToken): Promise<ServiceDashboardDTO[]> {
      let url_ = this.baseUrl + "/api/widget/paymentsservices?";
      if (receiptServiceId === null)
          throw new Error("The parameter 'receiptServiceId' cannot be null.");
      else if (receiptServiceId !== undefined)
          url_ += "receiptServiceId=" + encodeURIComponent("" + receiptServiceId) + "&";
      url_ = url_.replace(/[?&]$/, "");

      let options_: AxiosRequestConfig = {
          method: "GET",
          url: url_,
          headers: {
              "Accept": "text/plain"
          },
          cancelToken
      };

      return this.instance.request(options_).catch((_error: any) => {
          if (isAxiosError(_error) && _error.response) {
              return _error.response;
          } else {
              throw _error;
          }
      }).then((_response: AxiosResponse) => {
          return this.processPaymentsservices(_response);
      });
  }

  protected processPaymentsservices(response: AxiosResponse): Promise<ServiceDashboardDTO[]> {
      const status = response.status;
      let _headers: any = {};
      if (response.headers && typeof response.headers === "object") {
          for (const k in response.headers) {
              if (response.headers.hasOwnProperty(k)) {
                  _headers[k] = response.headers[k];
              }
          }
      }
      if (status === 200) {
          const _responseText = response.data;
          let result200: any = null;
          let resultData200  = _responseText;
          if (Array.isArray(resultData200)) {
              result200 = [] as any;
              for (let item of resultData200)
                  result200!.push(ServiceDashboardDTO.fromJS(item));
          }
          else {
              result200 = <any>null;
          }
          return Promise.resolve<ServiceDashboardDTO[]>(result200);

      } else if (status !== 200 && status !== 204) {
          const _responseText = response.data;
          return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      }
      return Promise.resolve<ServiceDashboardDTO[]>(null as any);
  }

  /**
   * @param patientId (optional) 
   * @return OK
   */
  packages(patientId?: number | undefined, cancelToken?: CancelToken): Promise<MedicalPackageDashboardDTO[]> {
      let url_ = this.baseUrl + "/api/widget/packages?";
      if (patientId === null)
          throw new Error("The parameter 'patientId' cannot be null.");
      else if (patientId !== undefined)
          url_ += "patientId=" + encodeURIComponent("" + patientId) + "&";
      url_ = url_.replace(/[?&]$/, "");

      let options_: AxiosRequestConfig = {
          method: "GET",
          url: url_,
          headers: {
              "Accept": "text/plain"
          },
          cancelToken
      };

      return this.instance.request(options_).catch((_error: any) => {
          if (isAxiosError(_error) && _error.response) {
              return _error.response;
          } else {
              throw _error;
          }
      }).then((_response: AxiosResponse) => {
          return this.processPackages(_response);
      });
  }

  protected processPackages(response: AxiosResponse): Promise<MedicalPackageDashboardDTO[]> {
      const status = response.status;
      let _headers: any = {};
      if (response.headers && typeof response.headers === "object") {
          for (const k in response.headers) {
              if (response.headers.hasOwnProperty(k)) {
                  _headers[k] = response.headers[k];
              }
          }
      }
      if (status === 200) {
          const _responseText = response.data;
          let result200: any = null;
          let resultData200  = _responseText;
          if (Array.isArray(resultData200)) {
              result200 = [] as any;
              for (let item of resultData200)
                  result200!.push(MedicalPackageDashboardDTO.fromJS(item));
          }
          else {
              result200 = <any>null;
          }
          return Promise.resolve<MedicalPackageDashboardDTO[]>(result200);

      } else if (status !== 200 && status !== 204) {
          const _responseText = response.data;
          return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      }
      return Promise.resolve<MedicalPackageDashboardDTO[]>(null as any);
  }

  /**
   * @param patientId (optional) 
   * @return OK
   */
  history(patientId?: number | undefined, cancelToken?: CancelToken): Promise<PatientHistoryItemDTO[]> {
      let url_ = this.baseUrl + "/api/widget/history?";
      if (patientId === null)
          throw new Error("The parameter 'patientId' cannot be null.");
      else if (patientId !== undefined)
          url_ += "patientId=" + encodeURIComponent("" + patientId) + "&";
      url_ = url_.replace(/[?&]$/, "");

      let options_: AxiosRequestConfig = {
          method: "GET",
          url: url_,
          headers: {
              "Accept": "text/plain"
          },
          cancelToken
      };

      return this.instance.request(options_).catch((_error: any) => {
          if (isAxiosError(_error) && _error.response) {
              return _error.response;
          } else {
              throw _error;
          }
      }).then((_response: AxiosResponse) => {
          return this.processHistory(_response);
      });
  }

  protected processHistory(response: AxiosResponse): Promise<PatientHistoryItemDTO[]> {
      const status = response.status;
      let _headers: any = {};
      if (response.headers && typeof response.headers === "object") {
          for (const k in response.headers) {
              if (response.headers.hasOwnProperty(k)) {
                  _headers[k] = response.headers[k];
              }
          }
      }
      if (status === 200) {
          const _responseText = response.data;
          let result200: any = null;
          let resultData200  = _responseText;
          if (Array.isArray(resultData200)) {
              result200 = [] as any;
              for (let item of resultData200)
                  result200!.push(PatientHistoryItemDTO.fromJS(item));
          }
          else {
              result200 = <any>null;
          }
          return Promise.resolve<PatientHistoryItemDTO[]>(result200);

      } else if (status !== 200 && status !== 204) {
          const _responseText = response.data;
          return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      }
      return Promise.resolve<PatientHistoryItemDTO[]>(null as any);
  }

  medicalanalysisresultmedicaltestrequestsbymedicalanalysisbacthid(medicalAnalysisBatchId?: number | undefined, cancelToken?: CancelToken): Promise<MedicalAnalysisResultMedicalAnalysisTestDTO[]> {
    let url_ = this.baseUrl + "/api/widget/medicalanalysisresultmedicaltestrequestsbymedicalanalysisbacthid?";
    if (medicalAnalysisBatchId === null)
        throw new Error("The parameter 'medicalAnalysisBatchId' cannot be null.");
    else if (medicalAnalysisBatchId !== undefined)
        url_ += "medicalAnalysisBatchId=" + encodeURIComponent("" + medicalAnalysisBatchId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "GET",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processMedicalanalysisresultmedicaltestrequestsbymedicalanalysisbacthid(_response);
    });
}

protected processMedicalanalysisresultmedicaltestrequestsbymedicalanalysisbacthid(response: AxiosResponse): Promise<MedicalAnalysisResultMedicalAnalysisTestDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(MedicalAnalysisResultMedicalAnalysisTestDTO.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<MedicalAnalysisResultMedicalAnalysisTestDTO[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<MedicalAnalysisResultMedicalAnalysisTestDTO[]>(null as any);
}
receiptservicegetbyids(receiptServiceIds: number[], cancelToken?: CancelToken): Promise<ReceiptServiceForPatientDTO[]> {
    let queryString = receiptServiceIds
        .map(id => `receiptServicesIds=${encodeURIComponent(id)}`)
        .join("&");

    let url_ = this.baseUrl + "/api/widget/receiptservicegetbyids";
    if (queryString) {
        url_ += "?" + queryString;
    }

    let options_: AxiosRequestConfig = {
        method: "GET",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processReceiptservicegetbyids(_response);
    });
}

protected processReceiptservicegetbyids(response: AxiosResponse): Promise<ReceiptServiceForPatientDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(ReceiptServiceForPatientDTO.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<ReceiptServiceForPatientDTO[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ReceiptServiceForPatientDTO[]>(null as any);
}
}

function throwException(message: string, status: number, response: string, headers: { [key: string]: any; }, result?: any): any {
  if (result !== null && result !== undefined)
      throw result;
  else
      throw new ApiException(message, status, response, headers, null);
}

function isAxiosError(obj: any): obj is AxiosError {
  return obj && obj.isAxiosError === true;
}